import type { ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';
import type { CreateDocumentInput } from '@xdocs/schemas';
import { LOCALES } from '@xdocs/i18n';
import { Button, Input, Select, Textarea, Toggle, cn, useI18n } from '@xdocs/ui';
import { safeFieldError } from '../../i18n/safeT.js';
import { optionalProp } from '../../lib/optionalProp.js';
import { useGenerateT } from './useGenerateT.js';
import styles from './GeneratePage.module.css';

const DOCUMENT_KINDS = ['book', 'article', 'report'] as const;

/** Every step panel shows the "STEP n OF m" eyebrow as its own first child
 * (mock 19), so each step component takes it as a prop rather than the
 * page rendering it once outside every card. */
export interface StepLabelProps {
  stepLabel: string;
}

/**
 * F-Estep ① "What are you making?" (mockup 19's `genType` choice grid). The
 * mockup's own option set (Book/Textbook/Guide/Fiction) isn't the real
 * `documentKind` enum — `packages/schemas` only defines
 * `book | article | report` — so this wires the mockup's *structure* (a
 * single-select grid setting the step's field) to the schema's real values
 * rather than inventing unsupported kinds.
 *
 * A dedicated `template.list` gallery (picking `templateId`) is a separate,
 * not-yet-built OPEN ISSUE: no such WS op exists in the frozen M1 contract,
 * so `templateId` stays optional and unset here.
 */
export function TemplateStep({ stepLabel }: StepLabelProps): ReactElement {
  const { t } = useGenerateT();
  const { setValue, watch } = useFormContext<CreateDocumentInput>();
  const kind = watch('kind');

  return (
    <div className={cn(styles['stepPanel'])}>
      <p className={cn(styles['stepEyebrow'])}>{stepLabel}</p>
      <h1 className={cn(styles['stepTitle'])}>{t('generate:step.template.title')}</h1>
      <div className={cn(styles['choiceGrid'])} role="radiogroup" aria-label={t('generate:step.template.title')}>
        {DOCUMENT_KINDS.map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={kind === option}
            className={cn(styles['choiceButton'], kind === option && styles['choiceButtonSelected'])}
            onClick={() => setValue('kind', option, { shouldValidate: true })}
          >
            {t(`generate:kind.${option}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

export function TopicStep({ stepLabel }: StepLabelProps): ReactElement {
  const { t } = useGenerateT();
  const { locale } = useI18n();
  const {
    register,
    formState: { errors },
  } = useFormContext<CreateDocumentInput>();

  return (
    <div className={cn(styles['stepPanel'])}>
      <p className={cn(styles['stepEyebrow'])}>{stepLabel}</p>
      <h1 className={cn(styles['stepTitle'])}>{t('generate:step.topic.title')}</h1>
      <Input
        label={t('generate:step.topic.titleLabel')}
        required
        {...optionalProp('error', safeFieldError(locale, errors.title?.message))}
        {...register('title')}
      />
      <Textarea
        label={t('generate:step.topic.briefLabel')}
        hint={t('generate:step.topic.briefHint')}
        {...optionalProp('error', safeFieldError(locale, errors.options?.brief?.message))}
        {...register('options.brief')}
      />
    </div>
  );
}

/** F-Estep ③ Sources. OPEN ISSUE: uploads (WS-FILES) and the public-domain
 * picker (F5, WS-CORPUS) are separate, not-yet-built workstreams — this
 * step is a fully skippable placeholder for M1. */
export function SourcesStep({ stepLabel }: StepLabelProps): ReactElement {
  const { t } = useGenerateT();
  return (
    <div className={cn(styles['stepPanel'])}>
      <p className={cn(styles['stepEyebrow'])}>{stepLabel}</p>
      <h1 className={cn(styles['stepTitle'])}>{t('generate:step.sources.title')}</h1>
      <p className={cn(styles['unavailableNotice'])}>{t('generate:step.sources.unavailable')}</p>
    </div>
  );
}

export function LengthStep({ stepLabel }: StepLabelProps): ReactElement {
  const { t } = useGenerateT();
  const { register } = useFormContext<CreateDocumentInput>();

  return (
    <div className={cn(styles['stepPanel'])}>
      <p className={cn(styles['stepEyebrow'])}>{stepLabel}</p>
      <h1 className={cn(styles['stepTitle'])}>{t('generate:step.length.title')}</h1>
      <Input
        type="number"
        min={1}
        max={200}
        label={t('generate:step.length.chapterCountLabel')}
        {...register('options.chapterCountTarget', {
          // An empty optional number field must submit as `undefined`, not
          // `NaN` (`valueAsNumber` on `''` produces `NaN`, which then fails
          // `createDocumentInput`'s `z.number()` check at submit time).
          setValueAs: (value: string) => (value === '' ? undefined : Number(value)),
        })}
      />
      <div className={cn(styles['grid2'])}>
        <Select
          label={t('generate:step.length.difficultyLabel')}
          options={[
            { value: 'basic', label: t('generate:difficulty.basic') },
            { value: 'intermediate', label: t('generate:difficulty.intermediate') },
            { value: 'advanced', label: t('generate:difficulty.advanced') },
            { value: 'mastery', label: t('generate:difficulty.mastery') },
          ]}
          {...register('difficultyLevel')}
        />
        <Select
          label={t('generate:step.length.languageLabel')}
          options={LOCALES.map((code) => ({ value: code, label: code.toUpperCase() }))}
          {...register('language')}
        />
      </div>
      <Toggle label={t('generate:step.length.illustrateLabel')} {...register('options.illustrate')} />
      <p className={cn(styles['unavailableNotice'])}>{t('generate:step.length.illustrateHint')}</p>
    </div>
  );
}

/** F16 persona picker. Same OPEN ISSUE as {@link TemplateStep} — no
 * `persona.list`-shaped WS op exists yet; `personaId` stays optional. */
export function PersonaStep({ stepLabel }: StepLabelProps): ReactElement {
  const { t } = useGenerateT();
  return (
    <div className={cn(styles['stepPanel'])}>
      <p className={cn(styles['stepEyebrow'])}>{stepLabel}</p>
      <h1 className={cn(styles['stepTitle'])}>{t('generate:step.persona.title')}</h1>
      <p className={cn(styles['unavailableNotice'])}>{t('generate:step.persona.unavailable')}</p>
    </div>
  );
}

export interface ReviewStepProps extends StepLabelProps {
  costChip: ReactElement;
  onSubmit: () => void;
}

export function ReviewStep({ stepLabel, costChip, onSubmit }: ReviewStepProps): ReactElement {
  const { t } = useGenerateT();
  const { watch } = useFormContext<CreateDocumentInput>();
  const values = watch();

  return (
    <div className={cn(styles['stepPanel'])}>
      <p className={cn(styles['stepEyebrow'])}>{stepLabel}</p>
      <h1 className={cn(styles['stepTitle'])}>{t('generate:step.review.title')}</h1>
      <ul className={cn(styles['summaryList'])}>
        <li className={cn(styles['summaryRow'])}>
          <span className={cn(styles['summaryLabel'])}>{t('generate:step.review.field.title')}</span>
          <span className={cn(styles['summaryValue'])}>{values.title || '—'}</span>
        </li>
        <li className={cn(styles['summaryRow'])}>
          <span className={cn(styles['summaryLabel'])}>{t('generate:step.review.field.kind')}</span>
          <span className={cn(styles['summaryValue'])}>{t(`generate:kind.${values.kind}`)}</span>
        </li>
        <li className={cn(styles['summaryRow'])}>
          <span className={cn(styles['summaryLabel'])}>{t('generate:step.review.field.difficulty')}</span>
          <span className={cn(styles['summaryValue'])}>{t(`generate:difficulty.${values.difficultyLevel}`)}</span>
        </li>
        <li className={cn(styles['summaryRow'])}>
          <span className={cn(styles['summaryLabel'])}>{t('generate:step.review.field.language')}</span>
          <span className={cn(styles['summaryValue'])}>{values.language.toUpperCase()}</span>
        </li>
        {values.options?.chapterCountTarget && (
          <li className={cn(styles['summaryRow'])}>
            <span className={cn(styles['summaryLabel'])}>{t('generate:step.review.field.chapters')}</span>
            <span className={cn(styles['summaryValue'])}>{values.options.chapterCountTarget}</span>
          </li>
        )}
      </ul>

      {costChip}

      <Button variant="primary" size="lg" onClick={onSubmit} disabled={!values.title.trim()}>
        {t('generate:step.review.submit')}
      </Button>
    </div>
  );
}
